import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Collapse, Input, Modal, Table, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import * as dayjs from "dayjs";

const { Text } = Typography;

const normalize = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const includesQuery = (value, query) => {
  const hay = normalize(value).toLowerCase();
  const needle = normalize(query).toLowerCase();
  if (!needle) return false;
  return hay.includes(needle);
};

const extractFullName = (record) => {
  const fromUserInfo = normalize(record?.userInfo?.name);
  if (fromUserInfo) return fromUserInfo;

  const fromFullName = normalize(record?.fullName);
  if (fromFullName) return fromFullName;

  const fromName = normalize(record?.name);
  if (fromName) return fromName;

  const firstLast = `${normalize(record?.firstName)} ${normalize(record?.lastName)}`.trim();
  if (firstLast) return firstLast;

  return "";
};

const extractEmail = (record) => normalize(record?.userInfo?.email || record?.email);

const extractPhone = (record) => normalize(record?.userInfo?.phone || record?.phone);

const getCreatedAt = (record) => record?.createdAt;

const getRecordId = (record) => record?.id;

const buildBaseLead = (record) => ({
  id: getRecordId(record),
  createdAt: getCreatedAt(record),
  name: extractFullName(record),
  email: extractEmail(record),
  phone: extractPhone(record),
  raw: record,
});

export const LeadSearchModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const quoteRequests = useSelector((state) => state.firestoreReducer.ordered.quoteRequests);
  const configuratorEntries = useSelector((state) => state.firestoreReducer.ordered.configuratorEntries);
  const businessCustomerRequests = useSelector((state) => state.firestoreReducer.ordered.businessCustomerRequests);
  const productFolderRequests = useSelector((state) => state.firestoreReducer.ordered.productFolderRequests);
  const contactRequests = useSelector((state) => state.firestoreReducer.ordered.contactRequests);
  const voiceMeetingRequests = useSelector((state) => state.firestoreReducer.ordered.voiceMeetingRequests);

  const [query, setQuery] = useState("");

  const datasets = useMemo(
    () => [
      { key: "quoteRequests", label: "Offerte aanvragen", rows: quoteRequests || [] },
      { key: "configuratorEntries", label: "Configurator aanvragen", rows: configuratorEntries || [] },
      { key: "businessCustomerRequests", label: "Zakelijke offerte aanvragen", rows: businessCustomerRequests || [] },
      { key: "productFolderRequests", label: "Productmap aanvragen", rows: productFolderRequests || [] },
      { key: "contactRequests", label: "Contactformulieren", rows: contactRequests || [] },
      { key: "voiceMeetingRequests", label: "Belafspraak aanvragen", rows: voiceMeetingRequests || [] },
    ],
    [
      quoteRequests,
      configuratorEntries,
      businessCustomerRequests,
      productFolderRequests,
      contactRequests,
      voiceMeetingRequests,
    ],
  );

  const resultsByDataset = useMemo(() => {
    const q = query.trim();
    if (!q) {
      return datasets.map((ds) => ({ ...ds, results: [] }));
    }

    return datasets.map((ds) => {
      const results = (ds.rows || [])
        .map((r) => buildBaseLead(r))
        .filter((lead) => includesQuery(lead.email, q) || includesQuery(lead.name, q) || includesQuery(lead.phone, q));
      return { ...ds, results };
    });
  }, [datasets, query]);

  const totalResults = useMemo(() => {
    return resultsByDataset.reduce((sum, ds) => sum + (ds.results?.length || 0), 0);
  }, [resultsByDataset]);

  const columns = useMemo(
    () => [
      {
        title: t("date"),
        dataIndex: "createdAt",
        render: (value) => {
          if (!value) return "-";
          return dayjs.unix(value).format("DD MMM 'YY HH:mm");
        },
        sorter: (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
        defaultSortOrder: "descend",
      },
      { title: t("name"), dataIndex: "name" },
      { title: t("email"), dataIndex: "email" },
      { title: t("phone"), dataIndex: "phone" },
    ],
    [t],
  );

  return (
    <Modal
      title="Zoek lead"
      open={open}
      onCancel={onClose}
      footer={
        <Button onClick={onClose}>
          {t("close")}
        </Button>
      }
      width={1000}
      destroyOnClose
    >
      <Input
        value={query}
        placeholder="Zoek op email, naam of telefoonnummer"
        prefix={<SearchOutlined />}
        onChange={(e) => setQuery(e.target.value)}
        allowClear
      />

      <div style={{ marginTop: 12 }}>
        <Text type="secondary">
          {totalResults > 0 ? `${totalResults} resultaat/resultaten` : query.trim() ? "Geen resultaten" : "Vul een zoekterm in"}
        </Text>
      </div>

      <div style={{ marginTop: 12 }}>
        <Collapse
          defaultActiveKey={["quoteRequests"]}
          items={resultsByDataset.map((ds) => ({
            key: ds.key,
            label: `${ds.label} (${ds.results.length})`,
            children: (
              <Table
                columns={columns}
                dataSource={ds.results}
                rowKey={(r) => `${ds.key}-${r.id || r.email || r.phone || r.name}`}
                pagination={{ pageSize: 10 }}
                size="small"
                scroll={{ x: 900 }}
              />
            ),
          }))}
        />
      </div>
    </Modal>
  );
};
